import { AppItem } from "@app/types";

/**
 * The text a filter query is matched against: the name, the tags, and the rating
 * written out as stars.
 *
 * The stars are why `***` works as a query — it is the only way to filter by rating,
 * and it matches three stars *or more*, because the string for a four-star item
 * contains the three-star one.
 */
export function itemHaystack(item: AppItem): string {
  let str = `${item.name} ${item.tags.join(" ")}`;
  if (item.rating) str += ` ${"*".repeat(item.rating)}`;
  return str;
}

export type Query = {
  inclusions: string[];
  exclusions: string[];
};

/**
 * Splits a filter box into terms that must appear and terms that must not.
 *
 * Space-separated, with a leading `-` marking an exclusion. A bare `-` is neither —
 * it is what a half-typed exclusion looks like, and treating it as an inclusion would
 * empty the list under the cursor.
 */
export function parseQuery(text: string): Query {
  const tokens = text
    .toLocaleLowerCase()
    .split(" ")
    .filter((x) => !!x);

  return {
    inclusions: tokens.filter((x) => x.charAt(0) !== "-"),
    exclusions: tokens
      .filter((x) => x.length > 1 && x.charAt(0) === "-")
      .map((x) => x.substring(1)),
  };
}

export function matchesQuery(item: AppItem, query: Query): boolean {
  const haystack = itemHaystack(item).toLocaleLowerCase();
  return (
    query.inclusions.every((x) => haystack.includes(x)) &&
    !query.exclusions.some((x) => haystack.includes(x))
  );
}

export function filterItems(items: AppItem[], filterText: string): AppItem[] {
  if (filterText.trim() === "") return items;
  const query = parseQuery(filterText);
  return items.filter((item) => matchesQuery(item, query));
}
